import { auth } from '@/plugins/firebaseClient'
import { useAuthStore } from '@/stores/auth'
import Vue from 'vue'
import VueRouter from 'vue-router'
import { routesCAN } from './routes'

Vue.use(VueRouter)

// Fix navigation duplicates
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
	return originalPush.call(this, location).catch(err => {
		if (err.name !== 'NavigationDuplicated') {
			throw err
		}
		return Promise.resolve()
	})
}

// Fallback to login if not logged in
const publicRoutes = ['Login', 'LoginRoot', 'EmailRedirected', 'ResetPassword']

const routes = [...routesCAN].map(route => ({
	...route,
	meta: {
		...route.meta,
		requiresAuth: route.meta?.mustBeLoggedIn !== false && !publicRoutes.includes(route.name),
	},
}))

// Create router instance
const router = new VueRouter({
	mode: 'history',
	base: process.env.BASE_URL,
	routes: routes,
	scrollBehavior(to, from, savedPosition) {
		return savedPosition || { x: 0, y: 0 }
	},
})

// Promise to get current auth state
const getCurrentUser = () => {
	return new Promise((resolve, reject) => {
		const unsubscribe = auth.onAuthStateChanged(async user => {
			unsubscribe()
			if (user) {
				try {
					const authStore = useAuthStore() // Create store instance first
					await authStore.initializeUser(user) // Then call method on instance
				} catch (err) {
					console.error('Error initializing user:', err)
				}
			}
			resolve(user)
		}, reject)
	})
}

// Navigation guard
router.beforeResolve(async (to, from, next) => {
	// Set page title
	document.title = to.meta.title || document.title

	try {
		// Special case: allow direct logout
		if (from.meta?.requiresAuth && !to.meta?.requiresAuth) {
			return next()
		}

		const currentUser = await getCurrentUser()
		const { useUserStore } = await import('@/stores/user')
		const userStore = useUserStore()
		const isAuthenticated = currentUser && userStore.userInfo?.isLogged

		// Handle route access based on auth requirements
		if (!isAuthenticated && to.meta.requiresAuth) {
			return next({
				name: 'Login',
				query: { redirect: to.fullPath },
			})
		}

		if (isAuthenticated && !to.meta.requiresAuth && to.name !== 'ResetPassword') {
			return next({ name: 'Search' })
		}

		return next()
	} catch (err) {
		console.error('Navigation guard error:', err)
		return next({ name: 'Login' })
	}
})

export default router
